import { Amplify } from 'aws-amplify'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  confirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  ConfirmSignInOutput,
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  resetPassword,
  ResetPasswordOutput,
  signIn as amplifySignIn,
  SignInOutput,
  signOut as amplifySignOut,
  updatePassword,
} from 'aws-amplify/auth'
import { CookieStorage, Hub } from 'aws-amplify/utils'
// Use the scoped @aws-amplify/auth/cognito import (not the aws-amplify re-export shim)
// so this resolves to the same pre-bundle chunk as attemptCompleteOAuthFlow and
// cacheCognitoTokens, ensuring setKeyValueStorage/setAuthConfig hit the correct singleton.
import {
  cognitoUserPoolsTokenProvider,
  signInWithRedirect,
} from '@aws-amplify/auth/cognito'
import { ResourcesConfig } from '@aws-amplify/core'
import type { AuthSession } from 'aws-amplify/auth'
import { callbackOrigin } from 'lib/cognito'
import { getCookieDomain } from 'lib/domain/getCookieDomain'

const VITE_COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN
const VITE_COGNITO_REGION = import.meta.env.VITE_COGNITO_REGION

// ─── MODULE-LEVEL: runs during import, before main.tsx's Amplify.configure() ──
// Amplify dispatches Hub events synchronously when the OAuth code exchange
// completes. If that exchange is triggered by Amplify.configure() (main.tsx),
// any useEffect-based listener would register too late. This module-level
// listener is guaranteed to be in place before configure() is called.
let crossDomainRedirectPending = false

/**
 * Safely extracts the origin from a value that may be either a full URL
 * (e.g. "https://zoox.../tempUrl/…") or a bare origin string.
 * Returns the input unchanged if URL parsing fails, so callers never throw.
 */
export function safeParseOrigin(value: string): string {
  try {
    return new URL(value).origin
  } catch {
    return value
  }
}

Hub.listen('auth', ({ payload }) => {
  if (payload.event === 'signInWithRedirect_failure') {
    const err = (payload as any).data?.error
    const cognitoConfig = (Amplify.getConfig()?.Auth as any)?.Cognito
    // Log the full error + stack so we can see exactly which assertTokenProviderConfig
    // call threw, or which Cognito error was returned.
    console.warn('[TSP Auth] signInWithRedirect_failure — full error:', err)
    console.warn('[TSP Auth] signInWithRedirect_failure — stack:', err?.stack)
    // JSON.stringify exposes empty strings ('') that would look truthy in object logs
    // but still fail !!value assertions inside Amplify.
    console.warn(
      '[TSP Auth] signInWithRedirect_failure — config values (JSON):',
      JSON.stringify({
        userPoolId: cognitoConfig?.userPoolId,
        userPoolClientId: cognitoConfig?.userPoolClientId,
        oauthDomain: cognitoConfig?.loginWith?.oauth?.domain,
      })
    )
    // Clear stale PKCE/OAuth state from CookieStorage so the next signInWithRedirect
    // attempt starts with fresh state values. A failed OAuth round-trip leaves mismatched
    // state cookies behind; without this cleanup every subsequent attempt also fails until
    // the user manually clears cookies. For OAuth-configured Amplify v6, signOut also
    // redirects through the Cognito /oauth2/logout endpoint, which terminates the Hosted
    // UI session and sends the user back to the app root (then on to /login) in a clean state.
    amplifySignOut().catch(() => {})
  }
  if (payload.event === 'customOAuthState') {
    const returnToUrl = (payload as any).data as string
    const targetOrigin = safeParseOrigin(returnToUrl)
    console.warn(
      '[TSP Auth] customOAuthState received, returnToUrl:',
      returnToUrl,
      'targetOrigin:',
      targetOrigin,
      'current:',
      globalThis.location.origin
    )
    if (returnToUrl && targetOrigin !== globalThis.location.origin) {
      crossDomainRedirectPending = true
      console.warn(
        '[TSP Auth] cross-domain redirect → replacing location with:',
        returnToUrl
      )
      globalThis.location.replace(returnToUrl)
    }
  }
})

function isPermanentSessionError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as any
  const name: string = e.name ?? e.__type ?? ''
  return name === 'NotAuthorizedException' || name === 'TokenRefreshException'
}

// Helper to get SSO session
const getSSOSession = (): AuthSession | null => {
  const ssoToken = sessionStorage.getItem('sso_token')
  if (!ssoToken) return null

  try {
    const payload = JSON.parse(atob(ssoToken.split('.')[1]))

    // Check if expired
    if (payload.exp * 1000 < Date.now()) {
      sessionStorage.removeItem('sso_token')
      sessionStorage.removeItem('sso_verified')
      return null
    }

    // Return mock AuthSession with the SSO token
    return {
      tokens: {
        idToken: {
          toString: () => ssoToken,
          payload: payload,
        },
        accessToken: {
          toString: () => ssoToken,
          payload: payload,
        },
      },
      credentials: undefined,
      identityId: undefined,
      userSub: payload.sub,
    } as any
  } catch (error) {
    console.error('Error parsing SSO token:', error)
    return null
  }
}

async function forceCleanSignout(cookieStorage: CookieStorage): Promise<void> {
  try {
    await amplifySignOut({ global: false })
  } catch {
    // signOut can fail when tokens are already invalid — fall through to manual clear
  }
  // amplifySignOut() may no-op on corrupt token state; clear the storage directly
  // so Amplify starts fresh. ) that document.cookie manipulation would miss.
  await cookieStorage.clear()
}

const COGNITO_AUTH_ERROR_NAMES = [
  'NotAuthorizedException',
  'TokenRevokedException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
  'RefreshTokenReuseException',
]

/** Mirrors Amplify's `isAuthenticationError`: the session cannot be refreshed. */
export function isCognitoAuthError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { name?: string; __type?: string }
  return COGNITO_AUTH_ERROR_NAMES.some(
    name => e.name?.startsWith(name) || e.__type === name
  )
}

export const useAmplifyAuth = (config: ResourcesConfig): AmplifyAuth => {
  const [user, setUser] = useState<FetchUserAttributesOutput | null>(null)
  const [amplifyConfigured, setAmplifyConfigured] = useState(false)
  const [isSSO, setIsSSO] = useState(false)

  // Created once and shared by fetchCurrentUser, the Hub listener, and the
  // setup effect so forceCleanSignout always uses the domain-aware storage instance.
  const cookieStorage = useMemo(() => {
    const cookieDomain = getCookieDomain(window.location.hostname)
    return new CookieStorage({
      domain: cookieDomain,
      secure: window.location.protocol === 'https:',
    })
  }, [])

  const fetchCurrentUser = useCallback(async () => {
    // First check for SSO session
    const ssoSession = getSSOSession()
    if (ssoSession) {
      setIsSSO(true)
      const payload = ssoSession.tokens?.idToken?.payload as any
      setUser({
        email: payload.email || sessionStorage.getItem('user_email') || '',
        sub: payload.sub || sessionStorage.getItem('user_sub') || '',
        name: payload.name || payload['cognito:username'],
        email_verified: 'true',
      } as FetchUserAttributesOutput)
      return
    }

    // Otherwise fetch from Amplify
    setIsSSO(false)
    try {
      const session = await fetchUserSessionInfo()
      const scope = session.tokens?.accessToken?.payload.scope
      if (scope?.includes('aws.cognito.signin.user.admin')) {
        const userAttributes = await fetchUserAttributes()
        setUser(userAttributes)
      } else if (session.tokens?.accessToken) {
        const at = session.tokens?.accessToken
        const res = await fetch(
          `https://${VITE_COGNITO_DOMAIN}.auth.${VITE_COGNITO_REGION}.amazoncognito.com/oauth2/userInfo`,
          {
            headers: { Authorization: `Bearer ${at}` },
          }
        )
        const userinfo = await res.json()
        setUser(userinfo)
      }
    } catch (err) {
      // Token revoked, expired, or network failure — leave user as null so
      // ProtectedRoutes redirects to /login instead of staying on blank screen.
      console.error('Failed to fetch current user:', err)
      if (isPermanentSessionError(err)) {
        // Clear invalid tokens from CookieStorage. Without this, LoginForm's
        // getCurrentUser() reads the stale cookies, sees a "valid" session, and
        // navigates to '/' — which kicks off the same fetchUserAttributes failure
        // and loops back to /login indefinitely.
        await forceCleanSignout(cookieStorage)
      }
      setUser(null)
    }
  }, [cookieStorage])

  useEffect(() => {
    Amplify.configure(config)

    // Scope cookies to .explorer.gd.tweddle.io (prod) or .explorer.[env].gd.tweddle.tech
    // (non-prod) so tokens are shared only between branded explorer subdomains.
    // Legacy and localhost hostnames receive undefined (same-origin only).
    cognitoUserPoolsTokenProvider.setKeyValueStorage(cookieStorage)
    fetchCurrentUser()
      .catch(console.error)
      .finally(() => {
        // Don't mark as configured if a cross-domain redirect is pending — doing so
        // would render SessionAuthCallback, which navigates to '/' and overrides it.
        if (!crossDomainRedirectPending) {
          setAmplifyConfigured(true)
        }
      })
  }, [config, fetchCurrentUser, cookieStorage])

  useEffect(() => {
    return Hub.listen('auth', ({ payload }) => {
      if (
        payload.event === 'tokenRefresh_failure' &&
        isCognitoAuthError((payload.data as { error?: unknown })?.error)
      ) {
        // Refresh token is permanently dead mid-session. Force-clear cookies
        // (prevents the LoginForm reload loop), then clear user so ProtectedRoutes
        // redirects to /login. Rate limits and network errors keep the session.
        void forceCleanSignout(cookieStorage).finally(() => setUser(null))
      }
    })
  }, [cookieStorage])

  const isConfigured = () => amplifyConfigured

  const signIn = async (username: string, password: string) =>
    amplifySignIn({ username, password })

  const signOut = async () => {
    // Clear SSO session if exists
    if (isSSO) {
      sessionStorage.removeItem('sso_token')
      sessionStorage.removeItem('sso_verified')
      sessionStorage.removeItem('user_email')
      sessionStorage.removeItem('user_sub')
      sessionStorage.removeItem('user_source')
      setIsSSO(false)
    } else {
      await amplifySignOut()
    }
    setUser(null)
  }

  const signInWithIdP = async (
    providerAlias?: string,
    returnToUrl?: string
  ) => {
    if (!providerAlias) return

    console.warn(
      '[TSP Auth] signInWithIdP — callbackOrigin:',
      callbackOrigin,
      'window.location.origin:',
      globalThis.location.origin,
      'returnToUrl:',
      returnToUrl
    )

    // Amplify v6 stores PKCE state in localStorage and rejects the OAuth callback if
    // it arrives on a different origin ("InvalidOriginException"). On branded domains
    // the callback is on the fixed shared domain, so we must initiate the OAuth flow
    // from there. Redirect to its /launch-auth page and let it call signInWithRedirect.
    if (callbackOrigin !== globalThis.location.origin) {
      const launchUrl = new URL('/launch-auth', callbackOrigin)
      launchUrl.searchParams.set('provider', providerAlias)
      // Pass the full return URL (origin + deep-link path) so the OAuth round-trip
      // can redirect back to the exact page the user originally requested.
      launchUrl.searchParams.set(
        'returnTo',
        returnToUrl ?? globalThis.location.origin
      )
      console.warn(
        '[TSP Auth] cross-origin — redirecting to launch-auth:',
        launchUrl.toString()
      )
      globalThis.location.href = launchUrl.toString()
      return
    }

    console.warn('[TSP Auth] same-origin — calling signInWithRedirect directly')
    return signInWithRedirect({
      provider: { custom: providerAlias },
      // Encode originating origin so the fixed callback URL can redirect back
      customState: globalThis.location.origin,
    })
  }

  // Modified to return SSO session if available
  const fetchUserSessionInfo = async (options?: {
    forceRefresh?: boolean
  }): Promise<AuthSession> => {
    const ssoSession = getSSOSession()
    if (ssoSession) return ssoSession
    if (options?.forceRefresh) {
      return fetchAuthSession({ forceRefresh: true })
    }
    return fetchAuthSession()
  }

  const refreshToken = async () => {
    const ssoSession = getSSOSession()
    // For SSO, just return the existing session (can't refresh externally issued tokens)
    if (ssoSession) return ssoSession
    return fetchAuthSession({ forceRefresh: true })
  }

  const confirmSignIn = (newPassword: string) =>
    amplifyConfirmSignIn({ challengeResponse: newPassword })

  const forgotPassword = async (username: string) => {
    return await resetPassword({ username })
  }

  const confirmForgotPassword = async (
    username: string,
    code: string,
    newPassword: string
  ) => {
    await confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword,
    })
    return true
  }

  const changeUserPassword = async (
    oldPassword: string,
    newPassword: string
  ) => {
    return updatePassword({ oldPassword, newPassword })
  }

  return {
    user,
    isConfigured,
    signIn,
    signOut,
    fetchUserSessionInfo,
    fetchAuthSession: fetchUserSessionInfo, // Add this alias
    refreshToken,
    confirmSignIn,
    fetchCurrentUser,
    forgotPassword,
    confirmForgotPassword,
    changeUserPassword,
    signInWithIdP,
    isSSO, // Expose SSO status
  }
}

export interface AmplifyAuth {
  user: FetchUserAttributesOutput | null
  isConfigured: () => boolean
  signIn: (username: string, password: string) => Promise<SignInOutput>
  signOut: () => Promise<void>
  fetchUserSessionInfo: (options?: {
    forceRefresh?: boolean
  }) => Promise<AuthSession>
  fetchAuthSession?: (options?: {
    forceRefresh?: boolean
  }) => Promise<AuthSession>
  refreshToken: () => Promise<AuthSession>
  confirmSignIn: (newPassword: string) => Promise<ConfirmSignInOutput>
  fetchCurrentUser: () => Promise<void>
  forgotPassword: (username: string) => Promise<ResetPasswordOutput>
  confirmForgotPassword: (
    username: string,
    code: string,
    newPassword: string
  ) => Promise<boolean>
  changeUserPassword: (
    oldPassword: string,
    newPassword: string
  ) => Promise<void>
  signInWithIdP: (providerAlias?: string, returnToUrl?: string) => Promise<void>
  isSSO?: boolean // Add this
}
